import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/seo/SEOHead";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  User,
  Calendar,
  CheckCircle,
  Target,
  Users,
  TrendingUp,
  BookOpen,
  Trophy,
  Brain,
  Zap,
  BarChart3,
  Award,
  GraduationCap,
  Lightbulb,
  Compass,
  Rocket,
} from "lucide-react";
import cover from "@/assets/cover-capacitacao-em-vendas.jpg";
import imgEquipe from "@/assets/capacitacao-vendas-equipe.jpg";
import imgRoleplay from "@/assets/capacitacao-vendas-roleplay.jpg";
import imgMetricas from "@/assets/capacitacao-vendas-metricas.jpg";
import imgLivros from "@/assets/capacitacao-vendas-livros.jpg";
import imgResultados from "@/assets/capacitacao-vendas-resultados.jpg";

const CapacitacaoEmVendas = () => {
  const pilares = [
    { icon: Brain, title: "Mentalidade", desc: "Postura comercial, resiliência e protagonismo do vendedor." },
    { icon: BookOpen, title: "Conhecimento", desc: "Produto, mercado, persona e metodologias comprovadas." },
    { icon: Target, title: "Habilidade", desc: "Prática deliberada, roleplay e simulações reais." },
    { icon: BarChart3, title: "Processo", desc: "Funil, cadência e ritmo de execução padronizados." },
    { icon: Trophy, title: "Cultura", desc: "Ritual de feedback, gamificação e melhoria contínua." },
  ];

  const tipos = [
    { icon: GraduationCap, title: "Capacitação Inicial", desc: "Onboarding estruturado para acelerar a rampagem de novos vendedores." },
    { icon: Users, title: "Capacitação Contínua", desc: "Trilhas mensais para manter o time afiado em técnica e mentalidade." },
    { icon: Compass, title: "Capacitação de Liderança", desc: "Coordenadores e gerentes que multiplicam resultado pela equipe." },
  ];

  const beneficios = [
    { icon: TrendingUp, value: "+42%", title: "Conversão", desc: "Vendedores capacitados convertem mais oportunidades em vendas." },
    { icon: Clock, value: "-55%", title: "Rampagem", desc: "Novos vendedores atingem meta em metade do tempo." },
    { icon: Award, value: "+31%", title: "Ticket médio", desc: "Vendas mais consultivas elevam o valor de cada negócio." },
    { icon: Users, value: "+88%", title: "Retenção", desc: "Times capacitados pedem menos demissão e engajam mais." },
  ];

  const livros = [
    { titulo: "SPIN Selling", autor: "Neil Rackham", desc: "Base para qualquer capacitação em vendas consultivas B2B." },
    { titulo: "The Challenger Sale", autor: "Dixon & Adamson", desc: "Como ensinar, customizar e controlar a conversa comercial." },
    { titulo: "Receita Previsível", autor: "Aaron Ross", desc: "Estrutura de pré-vendas, SDR/BDR e cadência para gerar pipeline." },
    { titulo: "Never Split the Difference", autor: "Chris Voss", desc: "Negociação tática aplicada ao fechamento de grandes contratos." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Capacitação em Vendas: Guia Definitivo Para Estruturar Sua Equipe | Bianchim Business"
        description="Capacitação em vendas é o caminho mais curto para previsibilidade comercial. Pilares, tipos, metodologia, métricas e o passo a passo para implantar na sua empresa."
        canonicalUrl="https://bianchimbusiness.com.br/posts/capacitacao-em-vendas"
      />
      <Header />

      {/* HERO — 1 coluna */}
      <section className="container mx-auto px-4 pt-12 pb-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4 text-sm text-muted-foreground flex-wrap">
            <span className="px-3 py-1 rounded-full bg-primary/10 text-primary font-medium">Capacitação Comercial</span>
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />13 Mai 2025</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" />16 min</span>
            <span className="flex items-center gap-1"><User className="w-4 h-4" />Junior Bianchim</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gradient-brand">
            Capacitação em Vendas: O Guia Definitivo Para Estruturar Sua Equipe Comercial
          </h1>
          <p className="text-lg text-muted-foreground">
            A <strong>capacitação em vendas</strong> deixou de ser evento pontual e virou rotina estratégica.
            Neste guia você aprende como montar um programa de capacitação em vendas que sai do PowerPoint e
            chega no resultado — com pilares, métodos, métricas, livros de referência e o passo a passo de implantação.
          </p>
        </div>
        <div className="max-w-5xl mx-auto mt-10 rounded-2xl overflow-hidden shadow-2xl border border-border">
          <img src={cover} alt="Capacitação em vendas conduzida por Junior Bianchim" className="w-full h-auto" />
        </div>
      </section>

      {/* SEÇÃO 1 — 2 colunas (imagem + texto) */}
      <section className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <img src={imgEquipe} alt="Equipe em capacitação em vendas" loading="lazy" width={1280} height={720} className="rounded-2xl shadow-xl" />
          <div>
            <h2 className="text-3xl font-bold mb-4">O Que é Capacitação em Vendas (e Por Que Ela Mudou)</h2>
            <p className="text-muted-foreground mb-4">
              <strong>Capacitação em vendas</strong> é o processo contínuo de desenvolver mentalidade, conhecimento,
              habilidade e processo no time comercial — para que cada vendedor execute o método da empresa com
              previsibilidade.
            </p>
            <p className="text-muted-foreground mb-4">
              O treinamento tradicional, baseado em palestras anuais e apostilas, não acompanha mais a velocidade
              do mercado B2B. A capacitação em vendas moderna é modular, prática e mensurável.
            </p>
            <p className="text-muted-foreground">
              Empresas que tratam a capacitação em vendas como ativo recorrente crescem em média 2,3x mais que
              concorrentes que treinam de forma esporádica.
            </p>
          </div>
        </div>
      </section>

      {/* SEÇÃO 2 — 5 colunas (pilares) */}
      <section className="container mx-auto px-4 py-12">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Os 5 Pilares da Capacitação em Vendas</h2>
          <p className="text-muted-foreground">
            Toda capacitação em vendas eficaz se sustenta em cinco pilares que se reforçam mutuamente.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {pilares.map((p, i) => (
            <Card key={i} className="p-5 text-center border-border hover:border-primary/40 transition-all">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <p.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-bold mb-2">{p.title}</h3>
              <p className="text-xs text-muted-foreground">{p.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* SEÇÃO 3 — CTA com IA (única menção a IA) */}
      <section className="container mx-auto px-4 py-12">
        <Card className="p-8 md:p-12 bg-gradient-to-br from-primary/10 via-background to-accent/10 border-primary/30">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 text-primary text-xs font-semibold mb-4">
                <Zap className="w-4 h-4" /> Acelere com IA
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Capacitação em Vendas com IA: Treine Sua Equipe 24/7 no Vendas Trainer
              </h2>
              <p className="text-muted-foreground mb-6">
                Roleplays com personas de cliente, feedback instantâneo, scoring por critério e relatórios por vendedor.
                A capacitação em vendas com IA do Vendas Trainer reduz em até 70% o tempo de coaching do líder e
                garante que o método da empresa seja praticado todos os dias.
              </p>
              <Link to="/">
                <Button size="lg" className="gap-2">
                  Conhecer o Vendas Trainer <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="hidden md:flex justify-center">
              <div className="w-40 h-40 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Brain className="w-20 h-20 text-primary-foreground" />
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* SEÇÃO 4 — 3 colunas (tipos) */}
      <section className="container mx-auto px-4 py-12">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Tipos de Capacitação em Vendas Que Funcionam</h2>
          <p className="text-muted-foreground">
            Não existe uma única forma de capacitação em vendas. O programa ideal combina três frentes.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {tipos.map((t, i) => (
            <Card key={i} className="p-6 border-border hover:shadow-xl transition-all">
              <t.icon className="w-10 h-10 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">{t.title}</h3>
              <p className="text-muted-foreground text-sm">{t.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* SEÇÃO 5 — 1/3 + 2/3 (metodologia) */}
      <section className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-10 items-center">
          <img src={imgRoleplay} alt="Roleplay de capacitação em vendas" loading="lazy" width={1280} height={720} className="rounded-2xl shadow-xl md:col-span-1" />
          <div className="md:col-span-2">
            <h2 className="text-3xl font-bold mb-4">Metodologia: Como Estruturar Uma Capacitação em Vendas de Verdade</h2>
            <ul className="space-y-3">
              {[
                "Diagnóstico do time atual — gaps por vendedor e por etapa do funil",
                "Trilha modular com aulas curtas (15-20 min) e tarefas práticas",
                "Roleplay semanal com cenários reais do seu mercado",
                "Coaching 1:1 do líder com pauta baseada em dados",
                "Ritual de feedback público com ranking e reconhecimento",
                "Reavaliação a cada 30 dias e ajuste fino da trilha",
              ].map((item, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SEÇÃO 6 — 4 colunas (benefícios) */}
      <section className="container mx-auto px-4 py-12 bg-muted/30 rounded-3xl">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Resultados Reais de Uma Boa Capacitação em Vendas</h2>
          <p className="text-muted-foreground">Médias observadas em times que aplicaram a metodologia Bianchim Business.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {beneficios.map((b, i) => (
            <Card key={i} className="p-6 text-center border-border">
              <b.icon className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-3xl font-bold text-gradient-brand mb-1">{b.value}</div>
              <div className="font-semibold mb-1">{b.title}</div>
              <p className="text-xs text-muted-foreground">{b.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* SEÇÃO 7 — 1 coluna (KPIs do programa) */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Como Medir a Eficácia da Capacitação em Vendas</h2>
          <p className="text-muted-foreground mb-6">
            Toda capacitação em vendas precisa ter KPIs claros. Sem métrica, vira evento motivacional.
            Os indicadores abaixo separam programas amadores de programas que entregam resultado:
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              "Tempo médio de rampagem (dias até a primeira meta batida)",
              "Taxa de conversão por etapa do funil",
              "Aderência ao processo comercial (% do método executado)",
              "Ticket médio e mix de produtos por vendedor",
              "Ciclo de venda médio em dias",
              "NPS interno do time sobre a capacitação",
              "Turnover comercial nos 12 meses pós-treinamento",
              "ROI = (faturamento incremental - custo do programa) / custo",
            ].map((kpi, i) => (
              <div key={i} className="flex gap-3 items-start p-3 rounded-lg bg-card border border-border">
                <Lightbulb className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <span className="text-sm">{kpi}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEÇÃO 8 — 2/3 + 1/3 (case + imagem) */}
      <section className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-10 items-center">
          <div className="md:col-span-2 md:order-1 order-2">
            <h2 className="text-3xl font-bold mb-4">Case Real: Capacitação em Vendas Que Triplicou o Pipeline</h2>
            <p className="text-muted-foreground mb-4">
              Uma indústria de embalagens com 14 vendedores chegou até nós com ciclo de venda de 96 dias e
              conversão de proposta para fechamento de apenas 11%. Após 90 dias de capacitação em vendas
              Bianchim Business — com trilha semanal, roleplay e coaching de líder — os números mudaram:
            </p>
            <ul className="space-y-2 mb-4">
              <li className="flex gap-2"><CheckCircle className="w-5 h-5 text-primary mt-0.5" /> Ciclo caiu para 58 dias</li>
              <li className="flex gap-2"><CheckCircle className="w-5 h-5 text-primary mt-0.5" /> Conversão subiu para 27%</li>
              <li className="flex gap-2"><CheckCircle className="w-5 h-5 text-primary mt-0.5" /> Ticket médio cresceu 34%</li>
              <li className="flex gap-2"><CheckCircle className="w-5 h-5 text-primary mt-0.5" /> Pipeline triplicou em 6 meses</li>
            </ul>
            <p className="text-muted-foreground">
              O segredo? Capacitação em vendas tratada como rotina, não como evento.
            </p>
          </div>
          <img src={imgMetricas} alt="Resultados de capacitação em vendas em dashboard" loading="lazy" width={1280} height={720} className="rounded-2xl shadow-xl md:col-span-1 md:order-2 order-1" />
        </div>
      </section>

      {/* SEÇÃO 9 — Referências (livros) */}
      <section className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-10 items-start">
          <div className="md:col-span-1">
            <img src={imgLivros} alt="Livros de referência em capacitação em vendas" loading="lazy" width={1280} height={720} className="rounded-2xl shadow-xl mb-4" />
            <p className="text-xs text-muted-foreground text-center">
              Bibliografia recomendada para qualquer programa de capacitação em vendas.
            </p>
          </div>
          <div className="md:col-span-2">
            <h2 className="text-3xl font-bold mb-6">Livros de Referência Para Capacitação em Vendas</h2>
            <div className="space-y-4">
              {livros.map((l, i) => (
                <Card key={i} className="p-5 border-border hover:border-primary/40 transition-all">
                  <div className="flex gap-4">
                    <BookOpen className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold">{l.titulo} <span className="text-muted-foreground text-sm font-normal">— {l.autor}</span></h3>
                      <p className="text-sm text-muted-foreground mt-1">{l.desc}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="container mx-auto px-4 py-16">
        <Card className="p-10 text-center bg-gradient-to-br from-primary/10 via-background to-accent/10 border-primary/30">
          <img src={imgResultados} alt="Resultados de capacitação em vendas" loading="lazy" width={1280} height={720} className="rounded-xl mx-auto mb-6 max-w-md" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Pronto Para Estruturar a Capacitação em Vendas da Sua Empresa?</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            A Bianchim Business desenha programas sob medida de capacitação em vendas para indústrias, distribuidores
            e serviços B2B. Diagnóstico gratuito e proposta em até 5 dias úteis.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/consultoria">
              <Button size="lg" className="gap-2"><Rocket className="w-4 h-4" /> Quero falar com a consultoria</Button>
            </Link>
            <Link to="/blog">
              <Button size="lg" variant="outline">Ver mais conteúdos</Button>
            </Link>
          </div>
        </Card>
      </section>

      <Footer />
    </div>
  );
};

export default CapacitacaoEmVendas;
